<template>
  <div class="flex w-full items-center justify-center space-x-3 px-4 pb-4">
    <div class="w-1/6">
      {{ type === "input" ? $t("COMMON.INPUT") : $t("COMMON.OUTPUT") }}:
    </div>
    <div class="flex min-w-0 flex-1 flex-col overflow-hidden">
      <tooltip
        ref="templatesTooltip"
        class="block max-w-full overflow-hidden"
        placement="bottom-start"
        trigger="click"
        hide-on-click="toggle"
        :on-click-outside="hideDropdown"
        append-to="parent"
        :arrow="false"
        sticky="reference"
        theme="none"
        interactive
        :popper-options="popperOptions"
      >
        <template #default>
          <lf-pill
            size="sm"
            class="template-pill group h-[22px] max-w-full cursor-pointer overflow-hidden"
            color-index="35"
            opaque
          >
            <div class="flex min-w-0 items-center gap-x-2">
              <span
                data-cy="template-name"
                class="truncate text-left text-xs font-normal"
              >
                {{ displayLabel }}
              </span>
              <icon-base
                :icon="IconArrowUp"
                height="10"
                width="10"
                class="text-flush-orange group-hover:visible"
                :class="
                  !templatesTooltip?.state.isVisible
                    ? 'invisible -translate-y-1 rotate-180'
                    : 'invisible'
                "
              />
            </div>
          </lf-pill>
        </template>
        <template #content>
          <ul
            class="shadow-lggrid h-auto max-h-100 max-w-80 grid-cols-1 gap-y-2 overflow-x-hidden overflow-y-auto rounded-md bg-white p-2 px-1 text-sm text-headline"
          >
            <li
              v-for="item in devices"
              :key="item.deviceId"
              v-tooltipable
              class="cursor-pointer truncate rounded p-2 text-xs whitespace-nowrap hover:bg-grey-hover"
              :class="{
                'bg-primary-background-active':
                  selectedDevice === item.deviceId,
              }"
              @click="setDevice(item)"
            >
              {{ item.label }}
            </li>
          </ul>
        </template>
      </tooltip>
    </div>
    <span
      data-cy="device-refresh"
      :title="$t('TWILIO.REFRESH_DEVICES')"
      class="cursor-pointer"
      @click="emit('device:refresh')"
    >
      <icon-base :icon="IconReload" view-box="4 4 16 16" />
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import IconArrowUp from "@/components/icons/IconArrowUp.vue";
import IconReload from "@/components/icons/IconReload.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { TippyComponent } from "vue-tippy";

const emit = defineEmits<{
  "device:select": [
    { deviceId: string; label: string },
    type: "input" | "output",
  ];
  "device:refresh": [];
}>();

const { devices, selectedDevice, type } = defineProps<{
  devices: MediaDeviceInfo[] | null;
  selectedDevice?: string;
  type: "input" | "output";
}>();

const { t } = useI18n();

const displayLabel = computed(
  () =>
    devices?.find(({ deviceId }) => deviceId === selectedDevice)?.label ??
    t("COMMON.DEFAULT")
);
const templatesTooltip = useTemplateRef<TippyComponent | null>(
  "templatesTooltip"
);
const popperOptions = ref<TippyComponent["popperOptions"]>({
  modifiers: [
    {
      name: "flip",
      options: { fallbackPlacements: ["top-start"] },
    },
  ],
});

const setDevice = (device: MediaDeviceInfo) => {
  emit("device:select", device, type);
  hideDropdown();
};

const hideDropdown = () => {
  templatesTooltip.value?.hide();
};
</script>
